import React, { useState } from 'react';
import { Gender, UserProfile } from '../types';
import { Button } from './Button';

interface OnboardingProps {
  onComplete: (profile: UserProfile, requestSubscribe: boolean) => void;
}

// Internal stages of the funnel
enum Stage {
  PROFILE = 0,
  COMMITMENT = 1,
  OFFER = 2
}

export const OnboardingStep: React.FC<OnboardingProps> = ({ onComplete }) => {
  const [stage, setStage] = useState<Stage>(Stage.PROFILE);
  
  // Form State
  const [name, setName] = useState('');
  const [gender, setGender] = useState<Gender | null>(null);
  const [targetGender, setTargetGender] = useState<Gender | null>(null);
  const [isCommitted, setIsCommitted] = useState(false);

  const handleNext = () => {
    if (stage === Stage.PROFILE && name && gender && targetGender) setStage(Stage.COMMITMENT);
    else if (stage === Stage.COMMITMENT && isCommitted) setStage(Stage.OFFER);
  };

  const handleFinalSubmit = (subscribe: boolean) => {
    if (name && gender && targetGender) {
      onComplete({ 
        name, 
        gender, 
        targetGender
      }, subscribe);
    }
  };

  // --- RENDER ---

  return (
    <div className="max-w-md mx-auto p-6 min-h-[80vh] flex flex-col justify-center">
      
      {/* Progress Bar */}
      <div className="w-full h-1 bg-slate-100 rounded-full mb-8">
          <div 
              className="h-full bg-brand-500 rounded-full transition-all duration-500"
              style={{ width: `${((stage + 1) / 3) * 100}%` }}
          ></div>
      </div>

      {/* STAGE 1: PROFILE (Identity) */}
      {stage === Stage.PROFILE && (
        <div className="animate-fade-in-up">
          <div className="mb-6">
              <span className="bg-brand-100 text-brand-700 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide">
                Get Started
              </span>
          </div>
          <h2 className="text-2xl font-extrabold text-slate-900 mb-2">Create your profile</h2>
          <p className="text-slate-500 mb-6">Customize your simulator identity.</p>
          
          <div className="space-y-4 mb-8">
            <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Your First Name</label>
                <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-brand-500 outline-none"
                    placeholder="Enter name"
                />
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase mb-2">I am a...</label>
                    <div className="space-y-2">
                        {[Gender.Male, Gender.Female, Gender.NonBinary].map(g => (
                            <button
                                key={g}
                                onClick={() => setGender(g)}
                                className={`w-full py-2 px-3 rounded-lg text-sm border transition-colors capitalize ${
                                    gender === g ? 'bg-brand-600 text-white border-brand-600' : 'bg-white border-slate-200 text-slate-600'
                                }`}
                            >
                                {g}
                            </button>
                        ))}
                    </div>
                </div>
                <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase mb-2">Practice with...</label>
                    <div className="space-y-2">
                        {[Gender.Male, Gender.Female, Gender.NonBinary].map(g => (
                            <button
                                key={g}
                                onClick={() => setTargetGender(g)}
                                className={`w-full py-2 px-3 rounded-lg text-sm border transition-colors capitalize ${
                                    targetGender === g ? 'bg-brand-600 text-white border-brand-600' : 'bg-white border-slate-200 text-slate-600'
                                }`}
                            >
                                {g}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
          </div>
          
          <Button fullWidth onClick={handleNext} disabled={!name || !gender || !targetGender}>Continue</Button>
        </div>
      )}

      {/* STAGE 2: COMMITMENT PLEDGE */}
      {stage === Stage.COMMITMENT && (
        <div className="animate-fade-in text-center">
            <div className="w-16 h-16 bg-brand-100 text-brand-600 rounded-full flex items-center justify-center text-3xl mx-auto mb-6">
                🤝
            </div>
            
            <h2 className="text-2xl font-extrabold text-slate-900 mb-4">Your Commitment</h2>
            
            <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100 mb-8 text-left relative overflow-hidden">
                <div className="absolute top-0 left-0 w-1 h-full bg-brand-500"></div>
                <p className="text-slate-600 text-sm leading-relaxed mb-4">
                    Mastering conversation isn't magic. It's a skill that requires practice.
                </p>
                <p className="text-slate-600 text-sm leading-relaxed mb-4">
                    By clicking below, you are committing to practicing at least <strong>5 minutes a day</strong> to achieve better relationships.
                </p>
                
                <label className="flex items-center gap-3 cursor-pointer group mt-6 pt-4 border-t border-slate-200">
                    <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-colors ${isCommitted ? 'bg-brand-600 border-brand-600' : 'border-slate-300 group-hover:border-brand-400'}`}>
                        {isCommitted && <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                    </div>
                    <input 
                        type="checkbox" 
                        className="hidden" 
                        checked={isCommitted} 
                        onChange={() => setIsCommitted(!isCommitted)} 
                    />
                    <span className={`font-bold text-sm ${isCommitted ? 'text-brand-700' : 'text-slate-500'}`}>
                        I commit to the process.
                    </span>
                </label>
            </div>

            <Button 
                fullWidth 
                onClick={handleNext} 
                disabled={!isCommitted}
            >
                Continue
            </Button>
        </div>
      )}

      {/* STAGE 3: OFFER (Paywall Intro) */}
      {stage === Stage.OFFER && (
        <div className="animate-fade-in-up text-center">
            <h2 className="text-2xl font-extrabold text-slate-900 mb-2">Unlock Full Mastery</h2>
            <p className="text-slate-500 mb-6 text-sm">Invest in your relationship skills today.</p>

            <div className="bg-white border-2 border-brand-500 rounded-2xl p-6 shadow-xl mb-6 relative overflow-hidden">
                <div className="absolute top-0 right-0 bg-brand-500 text-white text-[10px] font-bold px-3 py-1 rounded-bl-xl">
                    PRO
                </div>
                
                <div className="text-left space-y-3 mb-6">
                    {['Unlimited AI Conversations', 'Advanced "Hard" Mode Scenarios', 'Personalized Coaching Tips', 'Priority Support'].map((feat, i) => (
                        <div key={i} className="flex items-center gap-3">
                            <div className="w-5 h-5 rounded-full bg-brand-100 flex items-center justify-center text-brand-600 text-xs font-bold">✓</div>
                            <span className="text-slate-700 text-sm font-medium">{feat}</span>
                        </div>
                    ))}
                </div>

                <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="border border-slate-200 rounded-xl p-3 bg-slate-50 flex flex-col justify-center items-center">
                        <p className="text-xs text-slate-500 font-bold uppercase mb-1">Monthly</p>
                        <p className="text-xl font-extrabold text-slate-900">₦10,000</p>
                        <p className="text-[10px] text-slate-400">/ month</p>
                    </div>
                    <div className="border-2 border-brand-500 rounded-xl p-3 bg-brand-50 relative flex flex-col justify-center items-center">
                        <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-brand-600 text-white text-[8px] font-bold px-2 py-0.5 rounded-full uppercase shadow-sm whitespace-nowrap">
                            Best Value
                        </div>
                        <p className="text-xs text-brand-700 font-bold uppercase mb-1">Yearly</p>
                        <p className="text-xl font-extrabold text-brand-900">₦50,000</p>
                        <p className="text-[10px] text-brand-600">/ year</p>
                    </div>
                </div>

                <Button 
                    fullWidth 
                    onClick={() => handleFinalSubmit(true)}
                    className="mb-3"
                >
                    Subscribe Now
                </Button>
            </div>

            <button 
                onClick={() => handleFinalSubmit(false)}
                className="text-brand-600 font-medium text-sm hover:underline py-2 px-4"
            >
                Continue with 7-Day Free Trial
            </button>
            <p className="text-xs text-slate-400 mt-2">
                No credit card required for trial.
            </p>
        </div>
      )}

    </div>
  );
};